import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthContext';
import { defaultRouteForRole } from '@/shared/auth/RoleProtectedRoute';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

const MIN = 10;

/** Changement de mot de passe en self-service (tout utilisateur connecté). */
export default function ChangePasswordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const back = () => navigate(defaultRouteForRole(user?.role));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < MIN) { setError(`Le nouveau mot de passe doit contenir au moins ${MIN} caractères.`); return; }
    if (next !== confirm) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    setLoading(true);
    try {
      await api.put('/auth/me/password', { current_password: current, new_password: next });
      toast.success('Mot de passe modifié.');
      back();
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de modifier le mot de passe.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4" data-testid="change-password-page">
      <div className="w-full max-w-sm animate-fade-in">
        <button onClick={back} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 mb-8">Changer mon mot de passe</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-red-50 border border-red-100 text-red-600 text-sm" data-testid="change-password-error">
              {error}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="current" className="text-[13px] text-neutral-600">Mot de passe actuel</Label>
            <Input id="current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                   required className="h-10" data-testid="current-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="next" className="text-[13px] text-neutral-600">Nouveau mot de passe</Label>
            <div className="relative">
              <Input id="next" type={showPw ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)}
                     placeholder={`Au moins ${MIN} caractères`} required className="h-10 pr-10" data-testid="new-password-input" />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                      onClick={() => setShowPw(!showPw)}>
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="text-[13px] text-neutral-600">Confirmer</Label>
            <Input id="confirm" type={showPw ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   required className="h-10" data-testid="confirm-password-input" />
          </div>
          <Button type="submit" className="w-full h-10" disabled={loading} data-testid="change-password-submit">
            {loading ? 'Modification...' : 'Modifier'}
          </Button>
        </form>
      </div>
    </div>
  );
}
